import { Component, Input, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';

import { environment } from '../../../environments/environment';

import { MandatService, Mandat, MandatSingle } from '../../services/mandat.service';
import { ImageService, MandatImage, MandatImageTable } from '../../services/image.service';

@Component({
  selector: 'app-mandat-preview',
  templateUrl: './mandat-preview.component.html',
  styleUrls: ['./mandat-preview.component.scss'],
})
export class MandatPreviewComponent implements OnChanges {
  @Input() mandatId: number | null = null;

  mandat: Mandat | null = null;
  mandatImages: MandatImage[] = [];
  isLoading = false;

  imageStorage = environment.serverUrl + '/'; // images gehen über api

  private statusLabels: { [key: string]: string } = {
    A: 'Angezeigt',
    T: 'Teil-Erfasst',
    E: 'Erfasst',
    U: 'Überfällig',
    B: 'Bezahlt',
    AB: 'Ang/Bez',
    S: 'Storno',
    '8': 'Ausgenommen',
    '9': 'Error',
  };

  constructor(
    private mandatService: MandatService,
    private imageService: ImageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mandatId'] && this.mandatId) {
      this.loadMandat(this.mandatId);
    }
  }

  /**
   * load the mandat and its images for the readonly preview
   */
  private loadMandat(id: number): void {
    this.isLoading = true;
    this.mandat = null;
    this.mandatImages = [];

    this.mandatService.getMandatById(id).subscribe(
      (res: MandatSingle) => {
        this.mandat = res.data;

        // now load the images for the mandat (by aktenzeichen)
        const aktenzeichen = this.mandat?.Md_BelegNr || '';
        this.imageService.getImages('', aktenzeichen).subscribe(
          (imgRes: MandatImageTable) => {
            this.mandatImages = imgRes.data || [];
            this.isLoading = false;
            this.cdr.detectChanges();
          },
          () => {
            this.mandatImages = [];
            this.isLoading = false;
            this.cdr.detectChanges();
          }
        );
      },
      (error) => {
        console.error('Failed to load mandat preview:', error);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Readable label for a given status code
   */
  statusLabel(code: string): string {
    if (!code) return '';
    return this.statusLabels[code] || code;
  }

  /**
   * BOM vs Anzeige label
   */
  bomLabel(bom: number): string {
    return +bom === 1 ? 'BOM' : 'Anzeige';
  }

  /**
   * Tailwind classes for the colored status badge
   */
  statusBadgeClass(code: string): string {
    switch (code) {
      case 'A': // Angezeigt
        return 'bg-emerald-100 text-emerald-700';
      case 'B': // Bezahlt
        return 'bg-green-100 text-green-700';
      case 'AB': // Anzeige / Bezahlt
        return 'bg-teal-100 text-teal-700';
      case 'U': // Überfällig
        return 'bg-amber-100 text-amber-700';
      case 'E': // Erfasst
        return 'bg-sky-100 text-sky-700';
      case 'T': // Teil-Erfasst
        return 'bg-slate-100 text-slate-700';
      case 'S': // Storno
        return 'bg-gray-200 text-gray-600';
      case '8': // Ausgenommen
        return 'bg-orange-100 text-orange-700';
      case '9': // Error
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }

  /**
   * Parse Md_TatbestNr JSON and return comma-separated VSTV codes
   */
  formatVSTVCode(tatbestNr: any): string {
    if (!tatbestNr) return '';
    try {
      const arr = typeof tatbestNr === 'string' ? JSON.parse(tatbestNr) : tatbestNr;
      if (Array.isArray(arr)) {
        return arr.join(', ');
      }
      return String(tatbestNr);
    } catch {
      return String(tatbestNr);
    }
  }

  /**
   * open an image in a new window on click
   */
  openImageInNewWindow(imageSrc: string): void {
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(
        `<html><head><title>Image</title></head><body><img src="${this.imageStorage}img?f=${imageSrc}" ></body></html>`
      );
      newWindow.document.close();
    }
  }
}
